import _extends from '@babel/runtime/helpers/extends';
import React from 'react';
import PropTypes from 'prop-types';
import { p as props } from './componentEl-c8b73b10.js';
import { a as addClassNames, c as classNamePropTypes } from './classNames-cc2dcbf1.js';
import { a as addSpacingClasses, m as marginPropTypes, p as paddingPropTypes } from './spacing-7e57ffb0.js';
import { s as setIcon } from './icons-fde0c050.js';
import Icon from './Icon.js';

function SideNavSection({
  componentEl: Component,
  children,
  className,
  label,
  margin,
  padding,
  ...otherProps
}) {
  let cssClasses = ['st_sidenav__section'];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    padding,
    cssClasses
  });
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), label && /*#__PURE__*/React.createElement("h3", {
    className: "st_sidenav__heading"
  }, label), children);
}
SideNavSection.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** margin for section, if any */
  margin: marginPropTypes,
  /** margin for section, if any */
  padding: paddingPropTypes,
  /** optional label for nav section */
  label: PropTypes.string
};
SideNavSection.defaultProps = {
  componentEl: 'div',
  className: null,
  margin: {
    bottom: 'md'
  },
  padding: null,
  label: null
};
SideNavSection.displayName = 'SideNav.Section';

function SideNavItem({
  componentEl: Component,
  children,
  className,
  active,
  disabled,
  icon,
  wrap,
  color,
  ...otherProps
}) {
  let cssClasses = ['st_sidenav__clickable', `st_sidenav__clickable--color-${color}`];
  const clickableProps = {};
  cssClasses = addClassNames(className, cssClasses);
  if (active) {
    clickableProps['aria-current'] = 'page';
    cssClasses.push('st_sidenav__clickable--active');
  }
  if (wrap) cssClasses.push('st_sidenav__clickable--wrap');
  if (disabled) {
    clickableProps['aria-disabled'] = 'true';
    cssClasses.push('st_sidenav__clickable--disabled');
    if (Component === 'button') clickableProps.disabled = true;
  }
  const iconProps = {
    margin: {
      right: 'sm'
    },
    className: 'st_sidenav__icon'
  };
  const sideNavIcon = setIcon(Icon, icon, iconProps);
  return /*#__PURE__*/React.createElement("div", {
    className: "st_sidenav__item"
  }, /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps, clickableProps), sideNavIcon, children));
}
SideNavItem.propTypes = {
  /** dom element or component to use for clickable element */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** optional icon for token. A screentone icon component, e.g. IconStar */
  icon: PropTypes.any,
  /** whether item is active */
  active: PropTypes.bool,
  /** whether item is disabled */
  disabled: PropTypes.bool,
  /** color of item */
  color: PropTypes.oneOf(['ink', 'blurple']),
  /** whether text should wrap if it overflows  */
  wrap: PropTypes.bool
};
SideNavItem.defaultProps = {
  componentEl: 'a',
  className: null,
  icon: null,
  active: false,
  disabled: false,
  color: 'ink',
  wrap: false
};
SideNavItem.displayName = 'SideNav.Item';

/*
outermost wrapper for grid
*/
function SideNav({
  componentEl: Component,
  className,
  children,
  padding,
  border,
  ...otherProps
}) {
  let cssClasses = ['st_sidenav'];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    padding,
    cssClasses
  });
  if (border) cssClasses.push('st_sidenav--border');
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}
SideNav.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** padding for section, if any. Overrides default */
  padding: paddingPropTypes,
  /** whether to show a border */
  border: PropTypes.bool
};
SideNav.defaultProps = {
  componentEl: 'nav',
  className: null,
  padding: {
    vertical: 'sm'
  },
  border: true
};
SideNav.Section = SideNavSection;
SideNav.Item = SideNavItem;

export { SideNav as S };
